import type { TOC } from '@ember/component/template-only';
import { PrismBanner, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, gt, or } from 'ember-truth-helpers';
import GeneralAskErnest from 'lumen/components/general/ask-ernest';
import GeneralButtonsLiveshopButton from 'lumen/components/general/buttons/liveshop-button';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralDatepickersShopDatePicker from 'lumen/components/general/datepickers/shop-date-picker';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralReportScheduleReportsModals from 'lumen/components/general/report-schedule/reports-modals';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import ParityButtonGroup from 'lumen/components/parity/button-group';
import ParityMemberRateToggle from 'lumen/components/parity/member-rate-toggle';
import ParitySelections from 'lumen/components/parity/selections';
import ParityTablesTableParity from 'lumen/components/parity/tables/table-parity';
import type HotelParityListController from 'lumen/controllers/hotel/parity/list';
import can from 'lumen/helpers/can';
import hasFeature from 'lumen/helpers/has-feature';
import {
  ASK_ERNEST_PAGES,
  PARITY_PROMPTS,
} from 'lumen/utils/constants/ask-ernest-constants';

interface HotelParityListSignature {
  Args: {
    controller: HotelParityListController;
    model: unknown;
  };
}

<template>
  {{! template-lint-disable no-bare-strings }}
  <GeneralPageHeader aria-label="parity-primary-header">
    <div class="content-wrapper">
      <div class="flex flex-row justify-between">
        <div class="flex gap-400">
          <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
          <GeneralDatepickersDatepickerRange
            @displayEvents={{false}}
            @minDate={{@controller.minDateMoment}}
            {{! @glint-expect-error: Object literal may only specify known properties, and 'range' does not exist in type 'NamedArgs<...>'. }}
            @range={{@controller.dateRange}}
            @rangeChanged={{@controller.updateSelectedMonth}}
            @selectStandardRange={{true}}
            @standardRange="month"
          />
        </div>

        <div class="flex items-center justify-center gap-200">
          <ParityButtonGroup />
          <div class="flex flex-row items-center gap-200">
            <GeneralDropdownsDropdownExport
              @export={{@controller.handleExport}}
              @exportChoices={{@controller.exportChoices}}
              @isScheduleDisabled={{@controller.isScheduleDisabled}}
            />
            <GeneralButtonsLiveshopButton
              @hasLiveShop={{@controller.liveupdateEnabled}}
              @isParity={{true}}
              @onClick={{@controller.triggerLiveshop}}
            />

            {{#if (hasFeature "app.agentic_interface")}}
              <GeneralFilterBarDivider />

              <GeneralAskErnest
                @month={{@controller.monthMoment}}
                @page={{ASK_ERNEST_PAGES.PARITY}}
                @prompts={{PARITY_PROMPTS}}
              />
            {{/if}}
          </div>
        </div>
      </div>
    </div>
  </GeneralPageHeader>
  <GeneralPageHeader @isSmall={{true}} aria-label="parity-secondary-header">
    <div class="content-wrapper flex items-center">
      <ParityMemberRateToggle
        @isChecked={{@controller.showMemberRate}}
        @onChange={{@controller.toggleMemberRate}}
      />
      <GeneralFilterBarDivider />
      <ParitySelections
        @config={{@controller.model.config}}
        @isLimitedParityView={{@controller.isLimitedParityView}}
        @isRoomtypeRequestLoading={{@controller.isRoomtypeRequestLoading}}
        @rateParam={{@controller.rateParam}}
        @selectedLos={{@controller.los}}
        @selectedMaxPersons={{@controller.maxPersons}}
        @selectedMealPlan={{@controller.mealPlan}}
        @selectedMembershipType={{@controller.membershipType}}
        @selectedOTAGroupId={{@controller.OTAGroup}}
        @selectedOTAId={{@controller.OTAId}}
        @selectedPlatform={{@controller.platform}}
        @selectedPos={{@controller.pos}}
        @selectedRoomType={{@controller.roomType}}
        @selectionsChanged={{@controller.selectionsChanged}}
        @showParityAnalyticsData={{@controller.showParityAnalyticsData}}
      />
      <div class="update-timestamp text-200--regular text-nowrap">
        {{#if @controller.isPeriodsRequestLoading}}
          <div class="update-timestamp-loading-placeholder loading-pulse"></div>
        {{else if
          (and @controller.hasUpdateTimestamp @controller.showUpdateTime)
        }}
          <GeneralDatepickersShopDatePicker
            @extractDate={{@controller.extractDate}}
            @minDate={{@controller.minDateMoment}}
            @onSelect={{@controller.setExtractDate}}
          >
            {{t "general.updated"}}
            {{@controller.timestamp}}
          </GeneralDatepickersShopDatePicker>
        {{/if}}
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain class="overflow-hidden">
    <div class="content-wrapper main-content--new-layout h-full">
      {{#if
        (or
          @controller.isPeriodsRequestLoading
          @controller.isOtaHotelInfosRequestLoading
        )
      }}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{167}} />
      {{else}}
        {{#if @controller.showParityAnalyticsData}}
          <PrismBanner
            @bannerMessage="This tab lists all OTAs shopped for Parity Analytics. Only Lighthouse staff can see this, so don't show it to clients. Switch to another tab to leave this view."
            @bannerStyle="critical"
            @bannerTitle="Staff user only view"
            class="mb-500"
          />
        {{/if}}
        <ParityTablesTableParity
          @availableOTAs={{@controller.availableOTAs}}
          @hasDayLiveShop={{@controller.dayLiveupdateEnabled}}
          @isLoading={{@controller.isLoading}}
          @message={{@controller.noDataForSelection}}
          @parityType={{@controller.parityType}}
          @periods={{@controller.periods}}
          @showMemberRate={{@controller.showMemberRate}}
          @showMeta={{@controller.showMeta}}
        />
      {{/if}}
    </div>
  </GeneralPageMain>

  {{! @glint-expect-error: Argument of type '{ canCreateNewUser: boolean; ... }' is not assignable to parameter of type 'NamedArgs<...>'. }}
  <GeneralReportScheduleReportsModals
    @canCreateNewUser={{can "hotel.reports.create.other.new"}}
    @canCreateOtherReports={{can "hotel.reports.create.other.existing"}}
    @compsetAvailable={{gt
      @controller.hotelAndMarketService.availableCompsets.length
      1
    }}
    @isShowingScheduleModal={{@controller.scheduleReport}}
    @los={{@controller.los}}
    @maxPersons={{@controller.maxPersons}}
    @mealPlan={{@controller.mealPlan}}
    @onCloseScheduleModal={{@controller.closeScheduleModal}}
    @rateParam={{@controller.rateParam}}
    @showMeta={{@controller.showMeta}}
    @type={{@controller.type}}
  />

  {{outlet}}
</template> satisfies TOC<HotelParityListSignature>;
